import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Divider,
  message,
  Card,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment"; // Import moment for DatePicker

const { TextArea } = Input;
const { Option } = Select;

const categories = ["scientific", "societal", "economic", "environmental"];
const indicatorNames = [
  "CO2 Emissions Reduced",
  "Energy Saved (kWh)",
  "Publications Produced",
  "Patents Filed",
  "Jobs Created",
  "Training Sessions Held",
  "Stakeholders Engaged",
  "New Policies Influenced",
  "Technology Readiness Level Improved",
  "Environmental Footprint Decreased",
  "Market Uptake Initiated",
  "Citizen Awareness Raised",
  "Educational Materials Developed",
  "Software Tools Developed",
  "Mobility Improved",
  "Water Usage Reduced",
  "Renewable Energy Deployed",
  "Waste Recycled",
  "Innovation Score Increased",
  "Greenhouse Gas Intensity Reduced",
  // You can add more indicators as needed
];
const units = ["#", "%", "kg", "tonnes", "EUR", "hours"];

// KIPForm now accepts an optional 'kipId' prop for editing
const KIPEditForm = ({ token }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const { kipId } = useParams(); // Get kipId from URL for editing

  // Effect to fetch KIP data if kipId is present (edit mode)
  useEffect(() => {
    const fetchKIPData = async () => {
      try {
        const response = await axios.get(
          `https://eutracker.rdiup.com/v1/steps/${kipId}/`, // Adjust API endpoint
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        const kipData = response.data;
        console.log("kipdataaaa", kipData);

        if (!kipData) {
          message.error("No KIP data received from the server.");
          return; // Exit if no data
        }

        const formattedKipData = {
          ...kipData,
          // Safely access steps, default to empty array if undefined/null
          steps: Array.isArray(kipData.steps)
            ? kipData.steps.map((step) => ({
                ...step,
                date: moment(step.date),
                // Ensure indicators exist and are mapped
                indicators: Array.isArray(step.indicators)
                  ? step.indicators.map((ind) => ({
                      ...ind,
                      name: [ind.name], // For mode="tags"
                    }))
                  : [], // Default to empty array if no indicators
              }))
            : [], // Default to empty array if no steps
        };
        form.setFieldsValue(formattedKipData);
        message.success("KIP data loaded successfully!");
      } catch (error) {
        console.error("Error fetching KIP:", error);
        message.error("Failed to load KIP data.");
        // Optionally, redirect if KIP not found or error
        // navigate("/impacts");
      }
    };
    fetchKIPData();
  }, [kipId, form]);

  const handleChange = (value) => {
    setSelectedIndicators(value);
  };

  const onFinish = async (values) => {
    const formattedSteps = values.steps.map((step) => ({
      title: step.title || "Step Title",
      description: step.description,
      means_of_verification: step.means_of_verification,
      date: step.date.format("YYYY-MM-DD"),
      status: step.status,
      indicators: step.indicators.map((ind) => ({
        name: ind.name[0], // If it's an array, pick the first one
        value: parseFloat(ind.value),
        unit: ind.unit,
      })),
    }));

    try {
      const payload = {
        title: values.title,
        description: values.description,
        category: values.category,
        project: localStorage.getItem("project_id"),
        partner: localStorage.getItem("partner"),
        created_by: localStorage.getItem("partner"),

        steps: formattedSteps,
      };

      if (kipId) {
        // EDIT MODE: Send PUT/PATCH request
        /*    await axios.put(
          `https://eutracker.rdiup.com/v1/steps/update/${kipId}/`, // Adjust API endpoint for update
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        message.success("KIP updated successfully!"); */
        console.log("payloaddd", payload);
        fetch(`https://eutracker.rdiup.com/v1/steps/update/${kipId}/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(payload), // FIXED HERE
        })
          .then((res) => {
            if (!res.ok) throw new Error("Failed to update");
            return res.json();
          })
          .then((updated) => {
            console.log("Updated successfully", updated);
            message.success("KIP updated successfully!");
          })
          .catch((error) => {
            console.error("Error updating KIP:", error);
            message.error("Failed to update KIP");
          });
      } else {
        // CREATE MODE: Send POST request
        await axios.post(
          "https://eutracker.rdiup.com/v1/api/kips/create",
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        message.success("KIP created successfully!");
      }
      navigate("/impacts"); // Redirect to the impacts page after success
      form.resetFields(); // Reset form fields after successful submission
    } catch (error) {
      console.error("Error submitting KIP:", error);
      message.error(
        `Failed to ${kipId ? "update" : "create"} KIP: ${
          error.response?.data?.detail || error.message
        }`
      );
    }
  };

  return (
    <Card
      title={kipId ? "Edit Key Impact Pathway" : "Create Key Impact Pathway"}
      bordered={false}
      style={{ margin: 24 }}
    >
      <Form
        form={form}
        name="kip_form"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Please input the title!" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Please select a category!" }]}
            >
              <Select placeholder="Select category">
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Please input the description!" }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        {/* Add the overall KIP status here */}
        {/*  <Form.Item
          label="Overall KIP Status"
          name="status"
          rules={[
            { required: true, message: "Please select an overall status!" },
          ]}
        >
          <Select placeholder="Select overall status">
            <Option value="planned">Planned</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="completed">Completed</Option>
            <Option value="delayed">Delayed</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </Form.Item> */}

        <Divider orientation="left">Pathway Steps</Divider>

        <Form.List name="steps" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card size="small" key={key} style={{ marginBottom: 16 }}>
                  <Row gutter={16} align="middle">
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "title"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing step title",
                          },
                        ]}
                      >
                        <Input placeholder="Step Title" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "date"]}
                        rules={[{ required: true, message: "Missing date" }]}
                      >
                        <DatePicker
                          placeholder="Date"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing step description",
                          },
                        ]}
                      >
                        <Input placeholder="Step description" />
                      </Form.Item>
                    </Col>

                    <Col span={5}>
                      <Form.Item
                        {...restField}
                        name={[name, "means_of_verification"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing means of verification",
                          },
                        ]}
                      >
                        <Input placeholder="Means of verification" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      {/* This status field was missing from the inner indicator loop, assuming it's for the step status */}
                      <Form.Item
                        {...restField}
                        name={[name, "status"]} // This should likely be `name={[name, "status"]}` for the step, not indicator
                        rules={[
                          {
                            required: true,
                            message: "Please select a status!",
                          },
                        ]}
                      >
                        <Select placeholder="Select status">
                          <Option value="planned">Planned</Option>
                          <Option value="in_progress">In Progress</Option>
                          <Option value="completed">Completed</Option>
                          <Option value="delayed">Delayed</Option>
                          <Option value="cancelled">Cancelled</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>

                  <Divider orientation="left">Indicators</Divider>
                  <Form.List name={[name, "indicators"]}>
                    {(
                      indicatorFields,
                      { add: addIndicator, remove: removeIndicator }
                    ) => (
                      <>
                        {indicatorFields.map(
                          ({ key: indKey, name: indName, ...indRest }) => (
                            <Row
                              gutter={16}
                              key={indKey}
                              style={{ marginBottom: 8 }}
                            >
                              <Col span={6}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "name"]}
                                  rules={[{ required: true }]}
                                >
                                  <Select
                                    mode="tags"
                                    style={{ width: "100%" }}
                                    placeholder="Select or add indicator name(s)"
                                    onChange={handleChange} // Still useful for handling new tags
                                    showSearch
                                    allowClear
                                  >
                                    {indicatorNames.map((indicator) => (
                                      <Option key={indicator} value={indicator}>
                                        {indicator}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "value"]}
                                  rules={[{ required: true }]}
                                >
                                  <Input type="number" placeholder="Value" />
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "unit"]}
                                  rules={[{ required: true }]}
                                >
                                  <Select placeholder="Unit">
                                    {units.map((u) => (
                                      <Option key={u} value={u}>
                                        {u}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={2}>
                                <MinusCircleOutlined
                                  onClick={() => removeIndicator(indName)}
                                />
                              </Col>
                            </Row>
                          )
                        )}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addIndicator()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Indicator
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Card>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Step
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {kipId ? "Update KIP" : "Create KIP"}
          </Button>

          <Button
            onClick={() => navigate("/impacts")}
            style={{ marginLeft: 10 }}
          >
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default KIPEditForm;
